<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { fly } from 'svelte/transition';
	import { format } from 'svelte-i18n';
	import { Text } from '@Shelly/shelly-design-system';
	import FlowAnimation from './FlowAnimation_v2.svelte';

	export let dynamicSceneSuggestions: string[] = [];
	export let isMobileDevice = false;
	export let showExamplesOnMobile = true;
	export let showExamples = true;
	export let renderSuggestionWithTokens: (text: string) => string;

	const dispatch = createEventDispatcher<{ select: string }>();

	function handleChipClick(suggestion: string) {
		dispatch('select', suggestion);
	}
</script>

<div class="animation-wrapper">
	<FlowAnimation />
</div>

<div class="empty-state">
	{#if showExamples && (!isMobileDevice || showExamplesOnMobile)}
		<div class="examples-section" transition:fly={{ y: 30, duration: 280 }}>
			<Text class="examples-title"
				>{$format('ai_scene_chatbot_try_prompts', { default: 'Things you can do' })}</Text
			>
			<div class="examples">
				{#each dynamicSceneSuggestions as suggestion}
					<button class="example-chip" on:click={() => handleChipClick(suggestion)}>
						<span></span>{@html renderSuggestionWithTokens(suggestion)}<span></span>
					</button>
				{/each}
			</div>
			<!-- Step 1 onboarding popover anchor (example chips are always ≤3) -->
			<span class="chip-popover-wrapper step1-anchor-fallback">
				<slot name="popover-anchor"></slot>
			</span>
		</div>
	{/if}
</div>

<style>
	.animation-wrapper {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 140px;
		height: 153px;
		overflow: hidden;
		margin: 0 auto 16px;
	}

	.empty-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: flex-start;
		text-align: center;
		color: var(--color-text-secondary, #888);
	}

	/* :global() required to style canvas element rendered by FlowAnimation_v2 child component */
	.animation-wrapper :global(canvas) {
		width: 100%;
		height: 100%;
		object-fit: contain;
	}

	.examples-section {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 8px;
		width: 100%;
	}

	/* :global() required to style Text component passed as slot content */
	.examples-section :global(.examples-title) {
		font-size: 16px;
		color: white;
		font-weight: bold;
	}

	.examples {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 8px;
		width: 100%;
	}

	.chip-popover-wrapper {
		position: relative;
		display: inline-flex;
	}

	/* Step 1 onboarding popover anchor (example chips are always ≤3) */
	.chip-popover-wrapper.step1-anchor-fallback {
		display: block;
		width: 100%;
		min-height: 1px;
	}

	.chip-popover-wrapper :global(.popover-element) {
		position: absolute !important;
		inset: 0;
		display: block !important;
	}

	.example-chip {
		display: inline-flex;
		flex-wrap: wrap;
		justify-content: center;
		align-items: center;
		width: auto;
		max-width: 90%;
		background: rgba(14, 16, 19, 2);
		border: none;
		border-radius: 12px;
		padding: 10px 12px;
		cursor: pointer;
		text-align: center;
		font-family: 'Inter', sans-serif;
		font-size: 13px;
		font-weight: 400;
		line-height: normal;
		color: rgba(255, 255, 255, 0.6);
		text-transform: none;
		box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
		backdrop-filter: blur(10px);
		transition:
			background-color 0.2s,
			transform 0.2s;
	}

	@media (min-width: 769px) {
		.example-chip {
			max-width: 332px;
		}
	}

	.example-chip:hover {
		background: rgb(26, 28, 36);
	}

	.example-chip .quote {
		flex-shrink: 0;
	}

	/*
	 * :global() required for tokens rendered via innerHTML in example chips.
	 * Overrides TokenStyles.svelte defaults for Figma M size styling.
	 */
	.example-chip :global(.suggestion-token) {
		gap: 6px;
		padding: 4px 8px;
		border-radius: 24px;
		font-family: 'Inter', sans-serif;
		font-weight: 600;
		line-height: 16px;
		white-space: nowrap;
	}

	.example-chip :global(.suggestion-token i) {
		font-size: 12px;
	}

	/* Token colors are now in TokenStyles.svelte */
</style>
